import Tags, { TagifyBaseReactProps } from "@yaireo/tagify/dist/react.tagify"; // React-wrapper file
import "@yaireo/tagify/dist/tagify.css"; // Tagify CSS
import styled from "styled-components";
import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";

interface Props extends TagifyBaseReactProps {
  disabled?: boolean;
  maxTagLength?: number;
}

const Hashtag = (props: Props) => {
  const { maxTagLength, onInput, onKeydown, settings, ...restProps } = props;

  const placeCaretToEnd = (
    inputElm: HTMLInputElement | HTMLTextAreaElement | HTMLElement
  ) => {
    if ("setSelectionRange" in inputElm && typeof inputElm.value === "string") {
      const len = inputElm.value.length;
      inputElm.setSelectionRange(len, len);
      return;
    }

    const selection = window.getSelection();
    if (!selection || !inputElm.firstChild) return;
    const range = document.createRange();
    const textLength = inputElm.textContent?.length ?? 0;
    range.setStart(inputElm.firstChild, textLength);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return (
    <Container disabled={props.disabled}>
      <Tags
        {...restProps}
        onInput={(event) => {
          if (maxTagLength) {
            const detail = (event.detail as any) ?? {};
            const inputElm = detail.inputElm as
              | (HTMLInputElement | HTMLTextAreaElement | HTMLElement)
              | undefined;
            const rawValue =
              typeof detail.value === "string"
                ? detail.value
                : (
                    inputElm as
                      | HTMLInputElement
                      | HTMLTextAreaElement
                      | undefined
                  )?.value ??
                  inputElm?.textContent ??
                  "";

            if (inputElm && rawValue.length > maxTagLength) {
              const trimmedValue = rawValue.slice(0, maxTagLength);

              if ("value" in inputElm) {
                (inputElm as HTMLInputElement | HTMLTextAreaElement).value =
                  trimmedValue;
              } else {
                inputElm.textContent = trimmedValue;
              }

              placeCaretToEnd(inputElm);
            }
          }

          onInput?.(event);
        }}
        onKeydown={(event) => {
          const detail = (event.detail as any) ?? {};
          const keyboardEvent = detail.event as KeyboardEvent | undefined;
          const inputValue =
            typeof detail.value === "string" ? detail.value : "";

          if (!keyboardEvent) {
            onKeydown?.(event);
            return;
          }

          const isTextInput =
            keyboardEvent.key.length === 1 &&
            !keyboardEvent.metaKey &&
            !keyboardEvent.ctrlKey &&
            !keyboardEvent.altKey;

          if (
            maxTagLength &&
            isTextInput &&
            inputValue.length >= maxTagLength
          ) {
            if (typeof keyboardEvent.preventDefault === "function") {
              keyboardEvent.preventDefault();
            }
            return;
          }

          onKeydown?.(event);
        }}
        readOnly={props.disabled ?? props.readOnly ?? false}
        settings={{
          delimiters: ",| |\\.",
          trim: false,
          ...(settings ?? {}),
        }}
        // tagifyRef={tagifyRef} // optional Ref object for the Tagify instance itself, to get access to  inner-methods
        // settings={settings}  // tagify settings object
        // defaultValue="a,b,c"
        // {...tagifyProps}   // dynamic props such as "loading", "showDropdown:'abc'", "value"
        // onChange={onChange}
      />
    </Container>
  );
};

const Container = styled.div<{ disabled?: boolean }>`
  max-width: 100%;
  flex: 1;
  &[disabled],
  &[disabled] .tagify {
    opacity: 1;
    color: ${({ theme }) =>
      theme.value === "light"
        ? `${theme.COLORS.WHITE}60`
        : theme.COLORS.LIGHTGRAY};
  }
  .tagify {
    border-radius: 2px;
    width: 100%;
    overflow-x: auto;
    background-color: ${({ disabled, theme }) =>
      disabled ? theme.COLORS.DISABLED : COLORS.WHITE};
    border: 1px solid
      ${({ disabled, theme }) =>
        disabled ? theme.COLORS.DISABLED_BORDER : "#cacaca"};
    color: ${({ disabled, theme }) =>
      disabled
        ? theme.value === "light"
          ? `${theme.COLORS.WHITE}60`
          : theme.COLORS.LIGHTGRAY
        : COLORS.BLACK};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")};
    filter: none;
    flex-wrap: wrap;
  }
  .tagify__tag > div > span {
    font-size: ${calcRem(14)};
  }

  /* .tagify_input {
    --input-color: white;
  } */
`;
export default Hashtag;
